import { useState } from "react";
import { Zap, Instagram, Twitter, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    toast.success("Subscribed. Watch your inbox for weekly PR tips.");
    setEmail("");
  };

  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-ink-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <a href="#top" className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-neon-lime text-ink-0 shadow-glow-lime">
                <Zap className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <span className="font-display text-2xl tracking-wider text-white">PULSE</span>
            </a>
            <p className="mt-3 text-sm text-white/60 max-w-sm">
              Adaptive training, real coaches, measurable progress. Built for athletes who want proof — not promises.
            </p>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md">
              <Input
                type="email"
                placeholder="you@athlete.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-input"
                aria-label="Email address"
                className="h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-neon-lime/40"
              />
              <Button
                type="submit"
                data-testid="footer-newsletter-submit-button"
                className="btn-lift h-11 px-5 rounded-xl bg-neon-lime text-ink-0 hover:bg-neon-lime hover:brightness-95 font-semibold"
              >
                Get weekly tips
              </Button>
            </form>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6">
            <FooterCol
              title="Product"
              links={[
                ["Workouts", "#workouts"],
                ["Progress", "#progress"],
                ["Trainers", "#trainers"],
                ["Pricing", "#pricing"],
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                ["About", "#"],
                ["Careers", "#"],
                ["Press", "#"],
                ["Contact", "#"],
              ]}
            />
            <FooterCol
              title="Resources"
              links={[
                ["Help Center", "#"],
                ["Blog", "#"],
                ["Privacy", "#"],
                ["Terms", "#"],
              ]}
            />
          </div>
        </div>

        <div className="divider-lime my-10" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Pulse Fitness Labs. All rights reserved. · <span className="text-white/70">Your next PR starts today.</span>
          </div>
          <div className="flex items-center gap-2">
            <SocialLink icon={Instagram} label="Instagram" />
            <SocialLink icon={Twitter} label="Twitter" />
            <SocialLink icon={Youtube} label="YouTube" />
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-white/45 mb-3">{title}</div>
    <ul className="space-y-2">
      {links.map(([label, href]) => (
        <li key={label}>
          <a href={href} className="text-sm text-white/75 hover:text-neon-lime transition-colors">
            {label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const SocialLink = ({ icon: Icon, label }) => (
  <a
    href="#"
    aria-label={label}
    className="btn-lift inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 hover:text-neon-lime hover:border-neon-lime/30"
  >
    <Icon className="h-4 w-4" />
  </a>
);
